import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'antd';
import { getImportBatchDetail } from 'api/group';
import type { TImportBatchDetail } from 'types/group';
import s from './s.module.less';
import klarityLogo from 'assets/logo/KlarityLogo.png';

type Props = {
    url: string;
    batchUid: string;
    onExit: () => void;
    onEmpty: () => void;
    onFound: () => void;
};

type ScanStep = {
    label: string;
    status: 'done' | 'active' | 'pending';
};

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="10" fill="#0E62F4" />
        <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ActiveDot = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="5" fill="#0E62F4" />
    </svg>
);

const PendingDot = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="5" fill="#D1D5DB" />
    </svg>
);

const InfoIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="8" stroke="#60A5FA" strokeWidth="2" />
        <path d="M10 9V14" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" />
        <circle cx="10" cy="7" r="1" fill="#60A5FA" />
    </svg>
);

const SCAN_STEPS = [
    'Connecting to website',
    'Identifying provider pages',
    'Extracting profile details',
    'Organizing results',
];

const POLL_INTERVAL = 2000;
const BANNER_DELAY = 60000;

const WebsiteScanningPage: React.FC<Props> = ({ url, batchUid, onExit, onEmpty, onFound }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [showBanner, setShowBanner] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval>>();
    const bannerTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const pollTimerRef = useRef<number>();
    const onEmptyRef = useRef(onEmpty);
    const onFoundRef = useRef(onFound);
    onEmptyRef.current = onEmpty;
    onFoundRef.current = onFound;

    useEffect(() => {
        setActiveIndex(0);
        timerRef.current = setInterval(() => {
            setActiveIndex((prev) => {
                if (prev >= SCAN_STEPS.length - 1) {
                    clearInterval(timerRef.current);
                    return prev;
                }
                return prev + 1;
            });
        }, 1500);

        return () => clearInterval(timerRef.current);
    }, [batchUid]);

    useEffect(() => {
        setShowBanner(false);
        bannerTimerRef.current = setTimeout(() => {
            setShowBanner(true);
        }, BANNER_DELAY);

        return () => clearTimeout(bannerTimerRef.current);
    }, [batchUid]);

    useEffect(() => {
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            const res = await getImportBatchDetail(batchUid);
            if (cancelled) return;

            const detail: TImportBatchDetail | undefined = res?.data?.data;
            const status = detail?.parseStatus;

            if (status === 'DONE') {
                onFoundRef.current();
            } else if (status === 'DONE_EMPTY') {
                onEmptyRef.current();
            } else if (status === 'FAILED') {
                onEmptyRef.current();
            } else {
                pollTimerRef.current = window.setTimeout(poll, POLL_INTERVAL);
            }
        };
        poll();

        return () => {
            cancelled = true;
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [batchUid]);

    const steps: ScanStep[] = SCAN_STEPS.map((label, i) => ({
        label,
        status: i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'pending',
    }));

    return (
        <div className={s.page}>
            <div className={s.topBar}>
                <img src={klarityLogo} alt="Klarity" className={s.logo} />
                <Button className={s.exitBtn} onClick={onExit}>
                    Exit
                </Button>
            </div>

            <div className={s.content}>
                <div className={s.logoCircle}>
                    <span className={s.logoLetter}>K</span>
                </div>

                <div className={s.stepList}>
                    {steps.map((step, i) => (
                        <div key={i} className={s.stepRow}>
                            <span className={s.stepIcon}>
                                {step.status === 'done' && <CheckIcon />}
                                {step.status === 'active' && <ActiveDot />}
                                {step.status === 'pending' && <PendingDot />}
                            </span>
                            <span
                                className={`${s.stepLabel} ${
                                    step.status === 'done'
                                        ? s.stepDone
                                        : step.status === 'active'
                                          ? s.stepActive
                                          : s.stepPending
                                }`}
                            >
                                {step.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {showBanner && (
                <div className={s.infoBanner}>
                    <InfoIcon />
                    <div className={s.infoContent}>
                        <div className={s.infoTitle}>This may take a moment...</div>
                        <div className={s.infoDesc}>
                            {`You can leave this page — we'll keep working in the background and notify you when your providers are ready.`}
                        </div>
                    </div>
                    <span className={s.infoExit} onClick={onExit}>
                        Exit
                    </span>
                </div>
            )}
        </div>
    );
};

export default WebsiteScanningPage;
